import { Component, ChangeDetectionStrategy, inject, signal, computed, effect, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  AlertComponent,
  ButtonDirective,
  CardBodyComponent,
  CardComponent,
  CardHeaderComponent,
  ColComponent,
  ContainerComponent,
  FormControlDirective,
  MultiSelectComponent,
  MultiSelectOptionComponent,
  RowComponent,
  SpinnerComponent
} from '@coreui/angular-pro';
import { AuthService } from 'auth';
import { TranslatePipe } from 'i18n';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

interface Country {
  code: string;
  name: string;
  states?: { code: string; name: string }[];
}

interface SelectOption {
  value: string;
  label: string;
}

@Component({
  selector: 'app-kyc-form-page',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    AlertComponent,
    CardComponent,
    CardHeaderComponent,
    CardBodyComponent,
    ContainerComponent,
    RowComponent,
    ColComponent,
    FormControlDirective,
    ButtonDirective,
    MultiSelectComponent,
    MultiSelectOptionComponent,
    SpinnerComponent,
    TranslatePipe
  ],
  templateUrl: './kyc-form-page.component.html',
  styles: [`
    :host {
      display: block;
      position: absolute;
      inset: 0;
      overflow-y: auto;
    }
    :host label {
      color: rgba(255, 255, 255, 0.9) !important;
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class KycFormPageComponent {
  private fb = inject(FormBuilder);
  private http = inject(HttpClient);
  private authService = inject(AuthService);
  private destroyRef = inject(DestroyRef);

  kycForm: FormGroup;
  loading = signal(false);
  submitting = signal(false);
  success = signal(false);
  error = signal<string | null>(null);

  countriesData = signal<Country[]>([]);
  countries = signal<SelectOption[]>([]);
  countriesLoaded = signal(false);
  countriesError = signal(false);

  constructor() {
    this.kycForm = this.fb.group({
      kyc_first_name: ['', Validators.required],
      kyc_last_name: ['', Validators.required],
      kyc_address: ['', Validators.required],
      kyc_address2: [''],
      kyc_city: ['', Validators.required],
      kyc_postcode: ['', Validators.required],
      kyc_country_code: ['', Validators.required],
      kyc_state_code: [''],
      kyc_phone: ['', Validators.required],
      kyc_birthday: ['', Validators.required]
    });

    // Reset state when country changes
    this.kycForm.get('kyc_country_code')?.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.kycForm.patchValue({ kyc_state_code: '' }, { emitEvent: false });
      });

    // Load countries + existing data
    this.loadCountriesThenData();
  }

  /** Extracts the string code from either a plain string or a MultiSelect option object */
  private resolveCode(value: any): string {
    if (!value) return '';
    if (typeof value === 'object') return value.value ?? '';
    return String(value);
  }

  computedStates = computed(() => {
    const code = this.resolveCode(this.kycForm.get('kyc_country_code')?.value);
    if (!code) return [];
    const country = this.countriesData().find(c => c.code === code);
    return country?.states?.filter(s => !!s.name).map(s => ({ value: s.code, label: s.name })) ?? [];
  });

  hasStates = computed(() => this.computedStates().length > 0);

  private loadCountriesThenData(): void {
    this.loading.set(true);
    this.http.get<Country[]>('/countries')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.countriesData.set(data);
          this.countries.set(data.map(c => ({ value: c.code, label: c.name })));
          this.countriesLoaded.set(true);
          this.loadExistingData();
        },
        error: () => {
          this.loading.set(false);
          this.countriesError.set(true);
        }
      });
  }

  private loadExistingData(): void {
    this.authService.getUser$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(user => {
        this.loading.set(false);
        if (!user) return;

        this.kycForm.patchValue({
          kyc_first_name: user.kyc_first_name || user.first_name || '',
          kyc_last_name: user.kyc_last_name || user.last_name || '',
          kyc_address: user.kyc_address || user.address || '',
          kyc_address2: user.kyc_address2 || user.address2 || '',
          kyc_city: user.kyc_city || user.city || '',
          kyc_postcode: user.kyc_postcode || user.postcode || '',
          kyc_country_code: user.kyc_country_code || user.country_code || '',
          kyc_phone: user.kyc_phone || user.mobile || '',
          kyc_birthday: user.kyc_birthday ?? ''
        });

        // Patch state AFTER country
        const stateCode = user.kyc_state_code || user.state_code || '';
        if (stateCode) {
          this.kycForm.patchValue({ kyc_state_code: stateCode }, { emitEvent: false });
        }
      });
  }

  onSubmit(): void {
    if (this.submitting()) return;
    if (this.kycForm.invalid) {
      this.kycForm.markAllAsTouched();
      return;
    }

    const raw = this.kycForm.value;
    const payload = {
      ...raw,
      kyc_country_code: this.resolveCode(raw.kyc_country_code),
      kyc_state_code: this.resolveCode(raw.kyc_state_code)
    };

    this.submitting.set(true);
    this.error.set(null);

    this.http.post('/kyc/submit', { user: payload })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.submitting.set(false);
          this.success.set(true);
        },
        error: (err) => {
          this.submitting.set(false);
          this.error.set(err.error?.errors?.join(', ') || err.error?.error || 'An error occurred.');
        }
      });
  }
}